import React, { useEffect, useState, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import {
  useMsal,
  useIsAuthenticated
} from "@azure/msal-react";
import { loginRequest } from "./authConfig";
import { useGlobal } from "./context/GlobalContext";

import Layout from "./components/Layout";
import PageSkeleton from "./components/PageSkeleton";
import PageTitle from "./components/PageTitle";
import ScrollToTop from "./components/ScrollToTop";
import ProtectedRoute from "./components/ProtectedRoute";
import "./App.css";
import { ToastContainer } from "react-toastify";
import { InteractionStatus, EventType, EventMessage } from "@azure/msal-browser";
import { scheduleAutoLogout } from "./Api/index";

// Lazy load all pages
const AccessManagementDashboard = lazy(() => import("./pages/AccessManagementDashboard"));
const CheckPlatFormAccess = lazy(() => import("./pages/CheckPlatFormAccess"));
const CheckSnowflakeAccess = lazy(() => import("./pages/CheckSnowflakeAccess"));
const AccessRequestQueuePage = lazy(() => import("./pages/AccessRequestQueuePage"));
const RequestDetailsPage = lazy(() => import("./pages/RequestDetailsPage"));
const SecurityGroups = lazy(() => import("./pages/SecurityGroups"));
const SecurableOwner = lazy(() => import("./pages/SecurableOwner"));
const AccountOwner = lazy(() => import("./pages/AccountOwner"));
const ServiceAccountOwner = lazy(() => import("./pages/ServiceAccountOwner"));
const Settings = lazy(() => import("./pages/Settings"));
const Profile = lazy(() => import("./pages/Profile"));
const CreateCustomRole = lazy(() => import("./pages/CreateCustomRole"));
const AccessMirroring = lazy(() => import("./pages/AccessMirroring"));

const AuthHandler: React.FC = () => {
  const { instance, accounts, inProgress } = useMsal();
  const { setRole, setUserId } = useGlobal();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [isEntraLoading, setIsEntraLoading] = useState(false);
  const [hasProcessedAuth, setHasProcessedAuth] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const isAuthenticated = useIsAuthenticated();

  // Add timeout to prevent infinite loading
  useEffect(() => {
    if (loading) {
      const timeout = setTimeout(() => {
        console.warn("Auth loading timeout - stopping loading state");
        setLoading(false);
        setHasProcessedAuth(true);
      }, 5000); // 5 second timeout
      return () => clearTimeout(timeout);
    }
  }, [loading]);

  const handleEntraLogin = async () => {
    if (!email) {
      alert("Please enter your email.");
      return;
    }
    setIsEntraLoading(true);
    sessionStorage.setItem("entra_email", email);
    try {
      await instance.loginRedirect({
        ...loginRequest,
        loginHint: email,
        prompt: "login",
      });
    } catch (loginErr: any) {
      // Persist the error so we can inspect after navigation
      try {
        sessionStorage.setItem(
          "msal_last_error",
          JSON.stringify({
            at: new Date().toISOString(),
            stage: "loginRedirect",
            name: loginErr?.name,
            message: loginErr?.message,
            errorCode: loginErr?.errorCode,
            errorMessage: loginErr?.errorMessage,
            stack: loginErr?.stack,
          })
        );
      } catch { }
      // Also log to console
      // eslint-disable-next-line no-console
      console.error("MSAL loginRedirect error:", loginErr);
      throw loginErr;
    } finally {
      setIsEntraLoading(false);
    }
  };

  useEffect(() => {
    const storedExpiry = sessionStorage.getItem("entra_token_expires_at");
    if (storedExpiry) {
      scheduleAutoLogout(storedExpiry);
    }
  }, []);

  useEffect(() => {
    // Verbose MSAL event logging + persist last error for post-redirect inspection
    const callbackId = instance.addEventCallback((event: EventMessage) => {
      if (
        event.eventType === EventType.LOGIN_FAILURE ||
        event.eventType === EventType.ACQUIRE_TOKEN_FAILURE ||
        event.eventType === EventType.HANDLE_REDIRECT_END
      ) {
        const err = (event as any)?.error;
        const payload = {
          at: new Date().toISOString(),
          eventType: event.eventType,
          interactionType: (event as any)?.interactionType,
          name: err?.name,
          message: err?.message,
          errorCode: err?.errorCode,
          errorMessage: err?.errorMessage,
          stack: err?.stack,
        };
        // eslint-disable-next-line no-console
        console.error("MSAL event error:", payload);
        try {
          sessionStorage.setItem("msal_last_error", JSON.stringify(payload));
        } catch { }
      }
    });

    // Global fallbacks
    const rej = (e: PromiseRejectionEvent) => {
      // eslint-disable-next-line no-console
      console.error("Unhandled promise rejection:", e.reason);
      try {
        sessionStorage.setItem(
          "msal_last_error",
          JSON.stringify({ at: new Date().toISOString(), stage: "unhandledrejection", reason: String(e.reason) })
        );
      } catch { }
    };
    const err = (e: ErrorEvent) => {
      // eslint-disable-next-line no-console
      console.error("Window error:", e.message);
      try {
        sessionStorage.setItem(
          "msal_last_error",
          JSON.stringify({ at: new Date().toISOString(), stage: "windowerror", message: e.message })
        );
      } catch { }
    };
    window.addEventListener("unhandledrejection", rej);
    window.addEventListener("error", err);

    return () => {
      if (callbackId) instance.removeEventCallback(callbackId);
      window.removeEventListener("unhandledrejection", rej);
      window.removeEventListener("error", err);
    };
  }, [instance]);

  // Only run auth check when on /auth route and MSAL is ready
  useEffect(() => {
    // CRITICAL: Only run if we're on /auth route
    if (location.pathname !== "/auth") {
      return;
    }
    
    // Prevent multiple executions
    if (hasProcessedAuth) return;
    
    // Wait for MSAL to be ready
    if (inProgress !== InteractionStatus.None) {
      return;
    }
    
    // Check if already authenticated via MSAL
    if (isAuthenticated) {
      const account = instance.getActiveAccount() || accounts[0];
      if (account) {
        // User is authenticated, try to get token and redirect
        setLoading(true);
        instance
          .acquireTokenSilent({ ...loginRequest, account })
          .then((response) => {
            const token = response.accessToken;
            const idToken = (response as any).idToken || "";
            sessionStorage.setItem("entra_token", token);
            if (idToken) sessionStorage.setItem("entra_id_token", idToken);
            sessionStorage.setItem("entra_name", account.name || "User");
            sessionStorage.setItem("entra_email", account.username || "unknown@example.com");

            const expiresOn =
              (response as any)?.expiresOn?.getTime?.() ??
              ((response as any)?.expiresOn instanceof Date
                ? (response as any)?.expiresOn.getTime()
                : null);
            const expiresAt =
              expiresOn || Date.now() + ((response as any)?.expiresIn || 3600) * 1000;
            sessionStorage.setItem(
              "entra_token_expires_at",
              expiresAt.toString()
            );
            scheduleAutoLogout(expiresAt);
            setHasProcessedAuth(true);

            // Fetch user data in background
            if (API_BASE_URL) {
              fetch(`${API_BASE_URL}/api/get-actors`, {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({ email: account.username }),
              })
                .then((res) => {
                  if (res.ok) {
                    return res.json();
                  } else if (res.status === 404) {
                    // Endpoint doesn't exist, try alternative or use defaults
                    console.warn("⚠️ /api/get-actors endpoint not found (404). Trying alternative approach...");
                    // Try to get user info from token claims or use email as fallback
                    return null;
                  } else {
                    return Promise.reject(new Error(`API ${res.status}`));
                  }
                })
                .then((data) => {
                  if (data) {
                    const role = data?.role || data?.data?.role;
                    const userId = data?.id || data?.user_id || data?.data?.id || data?.data?.user_id;
                    
                    if (role) {
                      setRole(role);
                      sessionStorage.setItem("role", role);
                    }
                    if (userId) {
                      setUserId(userId.toString());
                      sessionStorage.setItem("user_id", userId.toString());
                    }
                  } else {
                    // API endpoint not available - set defaults or try alternative
                    console.warn("⚠️ Could not fetch user data from API. Using defaults.");
                    // Set a default role if needed, or leave it empty
                    // The user_id might be obtained from the token or another source
                  }
                })
                .catch((err) => {
                  console.error("Failed to fetch user data:", err);
                  // Don't block login if user data fetch fails
                });
            }
            
            // Navigate to dashboard
            navigate("/access-management-dashboard", { replace: true });
          })
          .catch((err) => {
            console.error("🔴 Auth flow error:", err?.message || err);
            setHasProcessedAuth(true);
            setLoading(false);
            // Stay on auth page if token acquisition fails
          });
      } else {
        setHasProcessedAuth(true);
        setLoading(false);
      }
    } else {
      // Not authenticated, show login form
      setHasProcessedAuth(true);
    }
  }, [instance, accounts, setRole, setUserId, navigate, API_BASE_URL, inProgress, hasProcessedAuth, isAuthenticated, location.pathname]);

  // Show loading only when actually loading and on auth route
  if (location.pathname === "/auth") {
    if (inProgress !== InteractionStatus.None) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-50">
          <div className="text-center">
            <svg className="animate-spin h-12 w-12 text-[#32196c] mx-auto mb-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            <p className="text-gray-600 font-medium">Initializing...</p>
          </div>
        </div>
      );
    }

    if (loading || isEntraLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-50">
          <div className="text-center">
            <svg className="animate-spin h-12 w-12 text-[#32196c] mx-auto mb-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            <p className="text-gray-600 font-medium">Signing in with Entra ID...</p>
          </div>
        </div>
      );
    }
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img
              src="/assets/imgs/GACIFY_Logo_Capital.png"
              alt="GAC POINT Logo"
              className="h-20 w-auto"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "/assets/imgs/GACIFY_Logo_Capital.png";
              }}
            />
          </div>
          <p className="text-gray-600">Single Point for Granular Access Control</p>
        </div>
        <div className="shadow-xl border-gray-200 rounded-xl bg-white">
          <div className="text-center pt-6">
            <h2 className="text-2xl font-bold text-gray-900">Sign In</h2>
            <p className="text-gray-600 mt-1">Enter your email to continue</p>
          </div>
          <div className="p-6 space-y-6">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="mt-2 flex h-10 w-full rounded-md border border-gray-300 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-[#32196c] bg-white text-gray-900 placeholder:text-gray-400"
            />
            <button
              onClick={handleEntraLogin}
              disabled={isEntraLoading}
              className="w-full bg-[#32196c] hover:bg-[#4a2a8a] text-white rounded-md py-3 font-semibold flex items-center justify-center"
            >
              {isEntraLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>
                  Signing in with Entra ID...
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-building2 h-4 w-4 mr-2"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path><path d="M10 6h4"></path><path d="M10 10h4"></path><path d="M10 14h4"></path><path d="M10 18h4"></path></svg>
                  Sign in with Entra ID
                </>
              )}
            </button>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">Or continue with</span>
              </div>
            </div>
            {/* Placeholder for other login methods, e.g., SSO, disabled for now */}
            <button
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-300 rounded-md py-3 font-semibold flex items-center justify-center"
              disabled
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-shield-check h-4 w-4 mr-2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"></path><path d="m9 12 2 2 4-4"></path></svg>
              Sign in With Enterprise SSO
            </button>
            <div className="text-center text-xs text-gray-500">
              By signing in, you agree to our terms of service and privacy policy.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Gate initial navigation - only handles root path redirect
const StartGate: React.FC = () => {
  const { inProgress } = useMsal();
  const location = useLocation();
  
  // Only handle root path - if already on another route, do nothing
  if (location.pathname !== "/") {
    return null;
  }
  
  // Show loading only if MSAL is initializing
  if (inProgress !== InteractionStatus.None) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-[#32196c] mx-auto mb-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          <p className="text-gray-600 font-medium">Initializing...</p>
        </div>
      </div>
    );
  }
  
  // Check for existing session first (fastest check)
  const existingToken = sessionStorage.getItem("entra_token");
  const existingRole = sessionStorage.getItem("role");
  
  if (existingToken && existingRole) {
    // User has valid session, go to dashboard
    return <Navigate to="/access-management-dashboard" replace />;
  } else {
    // No session, go to auth page
    return <Navigate to="/auth" replace />;
  }
};

// Removed unused RedirectUnauthenticated component

const App: React.FC = () => {

  return (
    <Router>
      <ScrollToTop />
      <PageTitle />
      <Routes>
        {/* Root path handles redirection */}
        <Route path="/" element={<StartGate />} />

        <Route path="/auth" element={<AuthHandler />} />

        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<Navigate to="/access-management-dashboard" replace />} />
          <Route 
            path="access-management-dashboard" 
            element={
              <Suspense fallback={<PageSkeleton variant="dashboard" />}>
                <AccessManagementDashboard />
              </Suspense>
            } 
          />
          <Route 
            path="access-request-queue" 
            element={
              <Suspense fallback={<PageSkeleton variant="table" />}>
                <AccessRequestQueuePage />
              </Suspense>
            } 
          />
          <Route 
            path="requests/:id" 
            element={
              <Suspense fallback={<PageSkeleton variant="table" />}>
                <RequestDetailsPage />
              </Suspense>
            } 
          />
          <Route 
            path="check-platform-access" 
            element={
              <Suspense fallback={<PageSkeleton variant="default" />}>
                <CheckPlatFormAccess />
              </Suspense>
            } 
          />
          <Route 
            path="check-snowflake-access" 
            element={
              <Suspense fallback={<PageSkeleton variant="default" />}>
                <CheckSnowflakeAccess />
              </Suspense>
            } 
          />
          <Route 
            path="security-groups" 
            element={
              <Suspense fallback={<PageSkeleton variant="form" />}>
                <SecurityGroups />
              </Suspense>
            } 
          />

          <Route 
            path="securable-owner" 
            element={
              <Suspense fallback={<PageSkeleton variant="form" />}>
                <SecurableOwner />
              </Suspense>
            } 
          />
          {/* <Route 
            path="account-owner" 
            element={
              <Suspense fallback={<PageSkeleton variant="table" />}>
                <AccountOwner />
              </Suspense>
            } 
          /> */}
          <Route 
            path="service-account-owner" 
            element={
              <Suspense fallback={<PageSkeleton variant="form" />}>
                <ServiceAccountOwner />
              </Suspense>
            } 
          />
          <Route 
            path="settings" 
            element={
              <Suspense fallback={<PageSkeleton variant="form" />}>
                <Settings />
              </Suspense>
            } 
          />
          <Route 
            path="profile" 
            element={
              <Suspense fallback={<PageSkeleton variant="default" />}>
                <Profile />
              </Suspense>
            } 
          />
          <Route 
            path="create-custom-role" 
            element={
              <Suspense fallback={<PageSkeleton variant="form" />}>
                <CreateCustomRole />
              </Suspense>
            } 
          />
          <Route 
            path="access-mirroring" 
            element={
              <Suspense fallback={<PageSkeleton variant="default" />}>
                <AccessMirroring />
              </Suspense>
            } 
          />
        </Route>
      </Routes>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        style={{
          zIndex: 9999,
        }}
      />
    </Router>
  );
};

export default App;